import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  UserPlusIcon,
  ArrowPathIcon,
  TrashIcon,
  UserCircleIcon,
  XMarkIcon,
  EnvelopeIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { fetchApi } from '../utils/api';
import { getRoleColor } from '../utils/rbac';

interface TeamUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string | null;
  isActive: boolean;
  dateJoined?: string | null;
  lastLogin?: string | null;
}

const ROLE_CHOICES = ['Admin', 'Dentist', 'Receptionist', 'Assistant'] as const;

const PASSWORD_RULES = [
  { key: 'length', test: (p: string) => p.length >= 8 },
  { key: 'uppercase', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lowercase', test: (p: string) => /[a-z]/.test(p) },
  { key: 'number', test: (p: string) => /\d/.test(p) },
  { key: 'special', test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
] as const;

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed';
}

export default function TeamPage() {
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Add-user modal state
  const [showCreate, setShowCreate] = useState(false);
  const [createData, setCreateData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Dentist' as string,
    password: '',
  });
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Reset-password modal state
  const [resetTarget, setResetTarget] = useState<TeamUser | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchApi('/api/users/');
      setUsers(res.users || []);
    } catch (e) {
      setError(errMsg(e) || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const flash = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const initials = (u: TeamUser) =>
    `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`.toUpperCase() ||
    u.email?.[0]?.toUpperCase() ||
    '?';

  /* ─── Create user ─── */
  const handleCreate = async () => {
    setCreateError(null);
    if (!createData.email || !createData.password) {
      setCreateError('Email and password are required');
      return;
    }
    setCreateSaving(true);
    try {
      const res = await fetchApi('/api/users/create/', {
        method: 'POST',
        body: JSON.stringify(createData),
      });
      if (!res.success) {
        setCreateError(res.error || 'Failed to create user');
        return;
      }
      setShowCreate(false);
      setCreateData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'Dentist',
        password: '',
      });
      flash('success', res.message);
      loadUsers();
    } catch (e) {
      setCreateError(errMsg(e) || 'Failed to create user');
    } finally {
      setCreateSaving(false);
    }
  };

  /* ─── Update role / active ─── */
  const handleUpdate = async (u: TeamUser, patch: Record<string, unknown>) => {
    try {
      const res = await fetchApi(`/api/users/${u.id}/`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      if (!res.success) {
        flash('error', res.error || 'Failed to update user');
        return;
      }
      flash('success', res.message);
      loadUsers();
    } catch (e) {
      flash('error', errMsg(e) || 'Failed to update user');
    }
  };

  /* ─── Reset password ─── */
  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetError(null);
    setResetSaving(true);
    try {
      const res = await fetchApi(
        `/api/users/${resetTarget.id}/reset-password/`,
        {
          method: 'POST',
          body: JSON.stringify({ new_password: resetPassword }),
        }
      );
      if (!res.success) {
        setResetError(res.error || 'Failed to reset password');
        return;
      }
      flash('success', res.message);
      setResetTarget(null);
      setResetPassword('');
    } catch (e) {
      setResetError(errMsg(e) || 'Failed to reset password');
    } finally {
      setResetSaving(false);
    }
  };

  /* ─── Delete user ─── */
  const handleDelete = async (u: TeamUser) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      const res = await fetchApi(`/api/users/${u.id}/`, { method: 'DELETE' });
      if (!res.success) {
        flash('error', res.error || 'Failed to delete user');
        return;
      }
      flash('success', res.message);
      loadUsers();
    } catch (e) {
      flash('error', errMsg(e) || 'Failed to delete user');
    }
  };

  const isSelf = (u: TeamUser) =>
    u.id === localStorage.getItem('doctif_user_id');

  return (
    <>
      <Helmet>
        <title>Team - Doctif</title>
        <meta
          name="description"
          content="Manage your clinic team and permissions"
        />
      </Helmet>

      <div className="p-8">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-card-foreground mb-2">
              Team
            </h1>
            <p className="text-muted-foreground">
              Manage your clinic staff, roles and permissions
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <UserPlusIcon className="h-5 w-5" />
            Add Team Member
          </button>
        </div>

        {message && (
          <div
            className={`mb-6 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm ${
              message.type === 'success'
                ? 'border-green-500/30 bg-green-500/10 text-green-600'
                : 'border-red-500/30 bg-red-500/10 text-red-600'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5" />
            ) : (
              <XCircleIcon className="h-5 w-5" />
            )}
            {message.text}
          </div>
        )}

        {error && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600">
            <XCircleIcon className="h-5 w-5" />
            {error}
          </div>
        )}

        {/* Users table */}
        <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <UserCircleIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No team members yet</p>
              <p className="text-sm">
                Add your first team member to get started
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-bold">
                            {initials(u)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-card-foreground truncate">
                            {`${u.firstName} ${u.lastName}`.trim() || '—'}
                            {isSelf(u) && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (you)
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                            <EnvelopeIcon className="h-3.5 w-3.5" />
                            {u.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={u.role || ''}
                        disabled={u.role === 'Admin' || isSelf(u)}
                        onChange={(e) =>
                          handleUpdate(u, { role: e.target.value })
                        }
                        className={`rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed ${getRoleColor(u.role) || 'text-card-foreground'}`}
                      >
                        {ROLE_CHOICES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        disabled={isSelf(u)}
                        onClick={() =>
                          handleUpdate(u, { isActive: !u.isActive })
                        }
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          u.isActive
                            ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                            : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                        }`}
                        title={
                          isSelf(u)
                            ? 'You cannot change your own status'
                            : 'Toggle active status'
                        }
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${u.isActive ? 'bg-green-500' : 'bg-red-500'}`}
                        />
                        {u.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setResetTarget(u)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors"
                          title={`Reset password for ${u.email}`}
                        >
                          <KeyIcon className="h-5 w-5" />
                        </button>
                        {u.role !== 'Admin' && !isSelf(u) && (
                          <button
                            type="button"
                            onClick={() => handleDelete(u)}
                            className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            title={`Delete ${u.email}`}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Role legend */}
        <div className="mt-6 bg-card rounded-xl border border-border shadow-sm p-5">
          <h2 className="text-sm font-semibold text-card-foreground mb-3">
            What each role can do
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 h-2 w-2 rounded-full bg-red-500 flex-shrink-0" />
              <p>
                <span className="font-medium text-card-foreground">Admin</span>{' '}
                — full access, manage team and settings
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
              <p>
                <span className="font-medium text-card-foreground">
                  Dentist
                </span>{' '}
                — patients, records, appointments and billing
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
              <p>
                <span className="font-medium text-card-foreground">
                  Receptionist
                </span>{' '}
                — patients, appointments and billing (no medical records)
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 h-2 w-2 rounded-full bg-purple-500 flex-shrink-0" />
              <p>
                <span className="font-medium text-card-foreground">
                  Assistant
                </span>{' '}
                — view schedules and patients, draft records (no billing)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Create user modal ── */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close dialog"
            onClick={() => setShowCreate(false)}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm cursor-default"
          />
          <div className="relative w-full max-w-md bg-card rounded-2xl border border-border shadow-soft-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-card-foreground">
                Add Team Member
              </h2>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted/50 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {createError && (
              <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                <XCircleIcon className="h-5 w-5" />
                {createError}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="team-first-name"
                    className="block text-sm font-medium text-card-foreground mb-1"
                  >
                    First name
                  </label>
                  <input
                    type="text"
                    id="team-first-name"
                    value={createData.firstName}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        firstName: e.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <div>
                  <label
                    htmlFor="team-last-name"
                    className="block text-sm font-medium text-card-foreground mb-1"
                  >
                    Last name
                  </label>
                  <input
                    type="text"
                    id="team-last-name"
                    value={createData.lastName}
                    onChange={(e) =>
                      setCreateData({ ...createData, lastName: e.target.value })
                    }
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="team-email"
                  className="block text-sm font-medium text-card-foreground mb-1"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="team-email"
                  value={createData.email}
                  onChange={(e) =>
                    setCreateData({ ...createData, email: e.target.value })
                  }
                  placeholder="name@clinic.com"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              <div>
                <label
                  htmlFor="team-role"
                  className="block text-sm font-medium text-card-foreground mb-1"
                >
                  Role
                </label>
                <select
                  id="team-role"
                  value={createData.role}
                  onChange={(e) =>
                    setCreateData({ ...createData, role: e.target.value })
                  }
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  {ROLE_CHOICES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="team-password"
                  className="block text-sm font-medium text-card-foreground mb-1"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="team-password"
                  value={createData.password}
                  onChange={(e) =>
                    setCreateData({ ...createData, password: e.target.value })
                  }
                  placeholder="8+ chars, upper, lower, number, symbol"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {PASSWORD_RULES.map((r) => (
                    <span
                      key={r.key}
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.test(createData.password) ? 'bg-green-500/15 text-green-600' : 'bg-muted text-muted-foreground'}`}
                    >
                      {r.key}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={createSaving}
                  className="btn-primary flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {createSaving && (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  )}
                  Create User
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Reset password modal ── */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close dialog"
            onClick={() => setResetTarget(null)}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm cursor-default"
          />
          <div className="relative w-full max-w-md bg-card rounded-2xl border border-border shadow-soft-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-card-foreground">
                Reset Password
              </h2>
              <button
                type="button"
                onClick={() => setResetTarget(null)}
                className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted/50 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Set a new password for{' '}
              <span className="font-medium text-card-foreground">
                {resetTarget.email}
              </span>
            </p>

            {resetError && (
              <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                <XCircleIcon className="h-5 w-5" />
                {resetError}
              </div>
            )}

            <input
              type="password"
              id="team-reset-password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              placeholder="New password"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 mb-4"
            />

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={resetSaving}
                className="btn-primary flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {resetSaving && (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                )}
                Reset Password
              </button>
              <button
                type="button"
                onClick={() => setResetTarget(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
